// Панель поверх карты, которую человек отводит в сторону, чтобы увидеть
// заслонённое место (раздел «Квоты»).
//
// Сдвиг — от ДОМАШНЕГО места панели (левый верхний угол карты), а не
// абсолютные координаты: так панель на другом экране встаёт туда же
// относительно своего угла, а прижим к краям решает, сколько от сдвига
// остаётся. Панель не уходит за карту ни краем: брошенная наполовину за
// кромку, она выглядела бы пропавшей, а вернуть её было бы нечем.

import { useCallback, useEffect, useRef, useState } from 'react'
import type { KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent } from 'react'

export interface Offset { x: number; y: number }
export interface Box { left: number; top: number; width: number; height: number }

/** Текст в ручке, который человек выделяет мышью, а не тащит: заголовок,
 *  счётчик. Перетаскивание начинается с остальной ручки — значка и полей. */
export const PANEL_TEXT_ATTR = 'data-panel-text'

const NOT_A_GRIP = `button, input, select, textarea, a, label, [role=radio], [${PANEL_TEXT_ATTR}]`

/** Начинает ли нажатие на этом элементе перетаскивание. */
export function startsDrag(target: { closest(selector: string): unknown } | null): boolean {
  return !!target && !target.closest(NOT_A_GRIP)
}

/** Шаг сдвига стрелкой с клавиатуры, px. */
export const KEY_STEP = 24

/** Прижать сдвиг так, чтобы панель целиком осталась внутри карты и держала
 *  от каждой кромки тот же отступ, что от угла домашнего места. */
export function clampOffset(offset: Offset, panel: Box, container: Box, home: Offset): Offset {
  const minX = 0
  const minY = 0
  const maxX = Math.max(minX, container.width - panel.width - 2 * home.x)
  const maxY = Math.max(minY, container.height - panel.height - 2 * home.y)
  return {
    x: Math.round(Math.min(maxX, Math.max(minX, offset.x))),
    y: Math.round(Math.min(maxY, Math.max(minY, offset.y))),
  }
}

/** Сдвиг стрелкой; другая клавиша — `null`. */
export function keyOffset(offset: Offset, key: string): Offset | null {
  switch (key) {
    case 'ArrowLeft': return { x: offset.x - KEY_STEP, y: offset.y }
    case 'ArrowRight': return { x: offset.x + KEY_STEP, y: offset.y }
    case 'ArrowUp': return { x: offset.x, y: offset.y - KEY_STEP }
    case 'ArrowDown': return { x: offset.x, y: offset.y + KEY_STEP }
    default: return null
  }
}

function read(key: string): Offset {
  try {
    const v = JSON.parse(localStorage.getItem(key) ?? 'null')
    if (v && Number.isFinite(v.x) && Number.isFinite(v.y)) return { x: v.x, y: v.y }
  } catch { /* приватное окно, битое значение — панель на домашнем месте */ }
  return { x: 0, y: 0 }
}

function write(key: string, v: Offset) {
  try { localStorage.setItem(key, JSON.stringify(v)) } catch { /* удобство, не данные */ }
}

/**
 * Перетаскивание панели за ручку. `home` — отступ домашнего места от угла
 * карты, `enabled` — выключатель (на телефоне панель — нижний лист, её не
 * тащат). Положение помнит браузер (`storageKey`): это удобство одного
 * человека на одном устройстве, а не настройка профиля.
 */
export function useFloatingPanel({ storageKey, home, enabled, shape }: {
  storageKey: string
  home: Offset
  enabled: boolean
  /** Смена облика панели (свёрнута / развёрнута — другой элемент): прижим и
   *  наблюдение за размером перепривязываются к новому элементу. */
  shape?: string
}) {
  const panelRef = useRef<HTMLElement | null>(null)
  const [offset, setOffset] = useState<Offset>(() => read(storageKey))
  const [dragging, setDragging] = useState(false)
  // Перетаскивание идёт МИМО React: состояние на каждое движение мыши
  // перерисовывало бы всю страницу с картой и списками, и панель тащилась бы
  // рывками. Во время движения сдвиг живёт здесь и пишется прямо в стиль
  // элемента раз в кадр; в состояние он попадает один раз — на отпускании.
  const grab = useRef<{
    x: number; y: number; from: Offset; panel: Box; container: Box
    live: Offset; frame: number
  } | null>(null)

  const clamp = useCallback((o: Offset): Offset => {
    const panel = panelRef.current
    const container = panel?.offsetParent as HTMLElement | null
    if (!panel || !container) return o
    return clampOffset(
      o,
      { left: 0, top: 0, width: panel.offsetWidth, height: panel.offsetHeight },
      { left: 0, top: 0, width: container.clientWidth, height: container.clientHeight },
      home,
    )
  }, [home])

  // Окно сжали, содержимое панели выросло — панель возвращается в карту.
  useEffect(() => {
    if (!enabled) return
    const panel = panelRef.current
    const fit = () => {
      if (grab.current) return
      setOffset(o => {
        const c = clamp(o)
        return c.x === o.x && c.y === o.y ? o : c
      })
    }
    fit()
    window.addEventListener('resize', fit)
    const ro = typeof ResizeObserver !== 'undefined' && panel ? new ResizeObserver(fit) : null
    if (ro && panel) ro.observe(panel)
    return () => { window.removeEventListener('resize', fit); ro?.disconnect() }
  }, [enabled, clamp, shape])

  useEffect(() => { if (!dragging) write(storageKey, offset) }, [dragging, offset, storageKey])

  const finish = () => {
    const g = grab.current
    if (!g) return
    cancelAnimationFrame(g.frame)
    grab.current = null
    const el = panelRef.current
    if (el) {
      el.style.transform = translate(g.live)
      el.style.backdropFilter = ''
      el.style.setProperty('-webkit-backdrop-filter', '')
      el.style.willChange = ''
    }
    setOffset(g.live)
    setDragging(false)
  }

  const handleProps = enabled ? {
    onPointerDown: (e: ReactPointerEvent<HTMLElement>) => {
      if (e.button !== 0 || !startsDrag(e.target as HTMLElement)) return
      const el = panelRef.current
      const container = el?.offsetParent as HTMLElement | null
      if (!el || !container) return
      // Без этого браузер по тому же нажатию начинает ВЫДЕЛЯТЬ текст, и
      // перетаскивание вбок выделяет всю страницу.
      e.preventDefault()
      window.getSelection()?.removeAllRanges()
      e.currentTarget.setPointerCapture(e.pointerId)
      // Размеры меряются один раз: чтение раскладки на каждом движении
      // заставляло бы браузер пересчитывать её посреди кадра.
      grab.current = {
        x: e.clientX, y: e.clientY, from: offset, live: offset, frame: 0,
        panel: { left: 0, top: 0, width: el.offsetWidth, height: el.offsetHeight },
        container: { left: 0, top: 0, width: container.clientWidth, height: container.clientHeight },
      }
      // Размытие фона под панелью пересчитывается поверх карты в каждом кадре
      // движения — на время перетаскивания оно снимается (фон и так почти
      // непрозрачен).
      el.style.backdropFilter = 'none'
      el.style.setProperty('-webkit-backdrop-filter', 'none')
      el.style.willChange = 'transform'
      setDragging(true)
    },
    onPointerMove: (e: ReactPointerEvent<HTMLElement>) => {
      const g = grab.current
      if (!g) return
      g.live = clampOffset(
        { x: g.from.x + e.clientX - g.x, y: g.from.y + e.clientY - g.y }, g.panel, g.container, home)
      if (g.frame) return
      g.frame = requestAnimationFrame(() => {
        g.frame = 0
        const el = panelRef.current
        if (el && grab.current === g) el.style.transform = translate(g.live)
      })
    },
    onPointerUp: (e: ReactPointerEvent<HTMLElement>) => {
      if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId)
      finish()
    },
    onPointerCancel: finish,
    // Двойной щелчок по тексту выделяет слово и панель не возвращает.
    onDoubleClick: (e: ReactMouseEvent<HTMLElement>) => {
      if (startsDrag(e.target as HTMLElement)) setOffset({ x: 0, y: 0 })
    },
    onKeyDown: (e: ReactKeyboardEvent<HTMLElement>) => {
      if (e.target !== e.currentTarget) return
      if (e.key === 'Home') { e.preventDefault(); setOffset({ x: 0, y: 0 }); return }
      const next = keyOffset(offset, e.key)
      if (next) { e.preventDefault(); setOffset(clamp(next)) }
    },
  } : {}

  // Стиль отдаётся ВСЕГДА, а не только при ненулевом сдвиге: во время
  // перетаскивания сдвиг пишется в элемент напрямую, и стиль без transform
  // React не стал бы приводить к нулю при возврате панели на место.
  const style = enabled ? { transform: translate(offset) } : undefined

  return { panelRef, handleProps, style, dragging }
}

export function translate(o: Offset): string {
  return `translate(${o.x}px, ${o.y}px)`
}
